import { useCallback } from 'react'

import { QueryParams } from 'redux/api/Widgets'

import { DashboardFilter } from 'types/GlobalDashboardFilter'
import { DashboardStatus } from 'types/GlobalDashboardGroup'
import { Condition, FilterType } from 'types/GlobalKpiOption'

/* Hooks */
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useCustomer } from 'redux/hooks/Customer'
import { useDashboardGroupFromDashboardId } from 'redux/hooks/DashboardGroups'
import { useParams } from 'utils/hooks'
import { useTranslation } from 'language/translator'

import { OptionProps } from 'components_new/molecules/OptionsMenu'

import { getDashboardLimitReached } from 'utils/functions'

export const parseFilterQueryParams = (
  dashboardFilter: DashboardFilter
): QueryParams | null => {
  const dashboardFilterParams = dashboardFilter.map((filter) => ({
    attributeId: filter.attribute,
    condition: Condition.EQ,
    value: [
      typeof filter.value === 'string' && filter.value.includes('Saknar värde')
        ? null
        : filter.value
    ],
    type: FilterType.INPUT_VALUE
  }))

  if (dashboardFilterParams.length === 0) {
    return null
  }

  return { filter: dashboardFilterParams }
}

interface UseDashboardOptionsProps {
  onDelete: () => void
  onDuplicate: () => void
  onEditLayout: () => void
  onEditTitle: () => void
  onExport: () => void
  onMove: () => void
  onPublish: () => void
  onShare: () => void
  onShowActivity: () => void
}

export const useDashboardOptions = (
  props: UseDashboardOptionsProps
): OptionProps[] => {
  const {
    onDelete,
    onDuplicate,
    onEditLayout,
    onEditTitle,
    onExport,
    onMove,
    onPublish,
    onShare,
    onShowActivity
  } = props
  const { isHomepal } = useAccountSettings()

  // callbacks
  const handleEditLayout = useCallback(
    () => (onEditLayout ? onEditLayout() : () => {}),
    [onEditLayout]
  )
  const handleEditTitle = useCallback(() => onEditTitle(), [onEditTitle])
  const handleExport = useCallback(
    () => (onExport ? onExport() : () => {}),
    [onExport]
  )
  const handleDelete = useCallback(() => onDelete(), [onDelete])
  const handleDuplicate = useCallback(() => onDuplicate(), [onDuplicate])
  const handleMove = useCallback(() => onMove(), [onMove])
  const handlePublish = useCallback(() => onPublish(), [onPublish])
  const handleShare = useCallback(
    () => (onShare ? onShare() : () => {}),
    [onShare]
  )
  const handleShowActivity = useCallback(
    () => (onShowActivity ? onShowActivity() : () => {}),
    [onShowActivity]
  )

  // hooks
  const { t } = useTranslation()
  const customer = useCustomer()
  const params = useParams<{ id: string }>()
  const dashboardGroup = useDashboardGroupFromDashboardId(params.id)

  const isPublished = dashboardGroup?.status === DashboardStatus.PUBLISHED

  const dashboardLimitReached = getDashboardLimitReached(isHomepal, customer)

  if (!dashboardGroup) return []

  return [
    {
      disabled: dashboardGroup.status !== DashboardStatus.PUBLISHED,
      iconName: 'TrendingUpOutlined' as const,
      onClick: handleShowActivity,
      title: t('activity_overview', 'activity_overview'),
      tooltip:
        dashboardGroup.status !== DashboardStatus.PUBLISHED
          ? t('activity_overview', 'drafts_have_no_activity')
          : undefined
    },
    {
      iconName: 'DesignServicesOutlined' as const,
      onClick: handleEditLayout,
      title: t('global', 'edit')
    },
    {
      iconName: 'Download' as const,
      onClick: handleExport,
      title: t('global', 'export')
    },
    {
      divider: true,
      iconName: 'Share' as const,
      onClick: handleShare,
      title: t('global', 'share')
    },
    {
      iconName: 'Edit',
      onClick: handleEditTitle,
      title: t('global', 'change_name')
    },
    {
      iconName: 'DriveFileMoveOutlined',
      onClick: handleMove,
      title: t('global', 'move')
    },
    {
      disabled: dashboardLimitReached,
      divider: true,
      iconName: 'Duplicate',
      onClick: handleDuplicate,
      title: t('global', 'duplicate'),
      tooltip: dashboardLimitReached
        ? t('dashboards', 'dashboard_limit_reached_tooltip')
        : undefined
    },
    {
      disabled: !isPublished && dashboardLimitReached,
      iconName: isPublished ? 'Unpublished' : 'Published',
      onClick: handlePublish,
      title: isPublished ? t('global', 'unpublish') : t('global', 'publish'),
      tooltip:
        !isPublished && dashboardLimitReached
          ? t('dashboards', 'dashboard_limit_reached_tooltip')
          : undefined
    },
    {
      iconName: 'Delete',
      onClick: handleDelete,
      title: t('global', 'delete')
    }
  ]
}
